import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ArrowRight, Search, MessageCircle } from 'lucide-react';

interface SearchFormInput {
  query: string;
}

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const modeFromParams = searchParams.get('mode');
  const queryFromParams = (searchParams.get('query') || '').trim();
  const autoSubmit = searchParams.get('autoSubmit') === '1';

  const [mode, setMode] = useState<'search' | 'chat'>('search');
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<SearchFormInput>({
    defaultValues: {
      query: '',
    },
  });
  const navigate = useNavigate();
  const currentQuery = watch('query');
  const autoSubmittedRef = useRef(false);

  const initialMode = useMemo<'search' | 'chat'>(
    () => (modeFromParams === 'chat' ? 'chat' : 'search'),
    [modeFromParams],
  );

  const routeToSaved = useCallback((targetMode: 'search' | 'chat', rawQuery: string) => {
    const encodedQuery = encodeURIComponent(rawQuery.trim());
    if (targetMode === 'chat') {
      if (encodedQuery) {
        navigate(`/saved?mode=chat&chatQuery=${encodedQuery}`);
      } else {
        navigate('/saved?mode=chat');
      }
      return;
    }

    if (encodedQuery) {
      navigate(`/saved?query=${encodedQuery}`);
    } else {
      navigate('/saved');
    }
  }, [navigate]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    setValue('query', queryFromParams, { shouldValidate: false, shouldDirty: false, shouldTouch: false });
  }, [queryFromParams, setValue]);

  useEffect(() => {
    if (!autoSubmit) return;
    if (autoSubmittedRef.current) return;
    if (!queryFromParams) return;

    autoSubmittedRef.current = true;
    routeToSaved(initialMode, queryFromParams);
  }, [autoSubmit, initialMode, queryFromParams, routeToSaved]);

  const onSubmit: SubmitHandler<SearchFormInput> = (data) => {
    routeToSaved(mode, data.query);
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-background pb-12">
      <div className="flex flex-col items-center mb-6">
        <a href="/saved">
          <img src="/logo-brand.svg" alt="logo" className="w-10 h-10 mb-2" />
        </a>
        
        <p className="text-3xl sm:text-4xl font-light tracking-tight text-neutral-800">Find anything you saved</p>
      </div>

      <div className="w-full max-w-xl px-3 sm:px-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-neutral-100 w-full p-1.5 rounded-lg border border-neutral-300 shadow-sm">
            <div className="relative">
              <Input
                id="query"
                placeholder={mode === 'search' ? "Search saved webpages..." : "Ask about saved webpages..."}
                {...register('query', { required: 'Search query cannot be empty' })}
                className={`h-12 text-lg font-light pr-12 bg-neutral-200 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none shadow-none border-0 ${errors.query ? 'border-red-500' : ''}`}
              />
              <div className="absolute inset-y-0 right-1.5 flex items-center">
                <Button 
                  type="submit" 
                  size="icon"
                  disabled={isSubmitting || !currentQuery?.trim()}
                  className="h-9 w-9 rounded-full"
                >
                  <ArrowRight className="w-4 h-4"/>
                </Button>
              </div>
            </div>
            <div className="w-full pt-2 pb-1 px-0.5 flex justify-start items-start">
              <div className="flex justify-start items-start -mb-0.5">
                <ToggleGroup 
                  type="single" 
                  value={mode} 
                  onValueChange={(value) => value && setMode(value as 'search' | 'chat')}
                  className=""
                >
                  <ToggleGroupItem 
                    value="search" 
                    aria-label="Search mode"
                    className="text-xs gap-1.5 h-7 px-1.5 rounded-md"
                  >
                    <Search className="h-4 w-4 " />
                  </ToggleGroupItem>
                  <ToggleGroupItem 
                    value="chat" 
                    aria-label="Chat mode"
                    className="text-xs gap-1.5 h-7 px-1.5 rounded-md"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
          </div>
          {errors.query && (
            <p className="text-red-500 text-sm mt-2 px-2">{errors.query.message}</p>
          )}
        </form>
      </div>
    </div>
  );
} 
